import React, { Component, ReactNode, ComponentType } from 'react';
import { ErrorBoundaryProps, ErrorBoundaryState, FallbackProps, NotificationData } from './types';
import { ErrorLogger } from './ErrorLogger';

// Utility functions for safe error handling
const getErrorMessage = (error: Error | null): string => {
  if (!error) return 'Erro desconhecido';
  return error.message || 'Mensagem de erro não disponível';
};

const getErrorStack = (error: Error | null): string => {
  if (!error) return 'Stack trace não disponível';
  return error.stack || 'Stack trace não disponível';
};

const getComponentStack = (errorInfo: React.ErrorInfo | null): string => {
  if (!errorInfo) return 'Component stack não disponível';
  return errorInfo.componentStack || 'Component stack não disponível';
};

const getErrorId = (errorId: string | null): string => {
  return errorId || 'unknown';
};

const DefaultFallback: ComponentType<FallbackProps> = ({
  error,
  errorInfo,
  resetError,
  errorId,
  retryCount
}) => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-[400px] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg border border-red-200 p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-red-800">
              Oops! Algo deu errado
            </h3>
            <p className="text-sm text-red-700 mt-1">
              Ocorreu um erro inesperado. Nossa equipe foi notificada e estamos trabalhando para resolver.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <div className="bg-muted rounded p-3 text-sm">
            <div className="font-medium text-foreground mb-1">Detalhes do erro:</div>
            <div className="text-foreground font-mono text-xs break-all">
              {getErrorMessage(error)}
            </div>
            <div className="text-muted-foreground text-xs mt-2">
              ID: {getErrorId(errorId)} | Tentativas: {retryCount}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {resetError && (
            <button
              onClick={resetError}
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors hover:bg-red-700/90"
            >
              Tentar novamente
            </button>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleReload}
              className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Recarregar página
            </button>
            <button
              onClick={handleGoHome}
              className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Página inicial
            </button>
          </div>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-foreground hover:text-foreground">
              Detalhes técnicos (desenvolvimento)
            </summary>
            <div className="mt-2 p-2 bg-gray-900 text-green-400 text-xs rounded overflow-auto max-h-48">
              <div className="font-bold mb-1">Stack Trace:</div>
              <pre className="whitespace-pre-wrap break-all">
                {getErrorStack(error)}
              </pre>
              <div className="font-bold mb-1 mt-3">Component Stack:</div>
              <pre className="whitespace-pre-wrap break-all">
                {getComponentStack(errorInfo)}
              </pre>
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private errorLogger: ErrorLogger;
  private retryTimeoutId: NodeJS.Timeout | null = null;

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };

    this.errorLogger = ErrorLogger.getInstance();
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorId = this.state.errorId || this.generateErrorId();

    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Log error to service
    const loggedErrorId = this.errorLogger.logError(
      error,
      errorInfo,
      this.props.severity || 'medium',
      this.props.context || 'Unknown',
      this.state.retryCount,
      this.constructor.name
    );

    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo, loggedErrorId);
    }

    // Show notification if enabled
    this.showErrorNotification(error, this.props.context);
  }

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private showErrorNotification(error: Error, context?: string): void {
    try {
      // Check if notification system is available
      if (typeof window !== 'undefined' && (window as any).showNotification) {
        const notification: NotificationData = {
          type: 'error',
          title: 'Erro na aplicação',
          message: `Ocorreu um erro em ${context || 'um componente'}. Tente novamente.`,
          duration: 5000,
          action: {
            label: 'Recarregar',
            onClick: () => window.location.reload()
          }
        };

        (window as any).showNotification(notification);
      }
    } catch (notificationError) {
      // Ignore notification errors to prevent infinite loops
      console.warn('Failed to show error notification:', notificationError);
    }
  }

  private resetError = (): void => {
    const maxRetries = this.props.maxRetries || 3;

    if (this.state.retryCount >= maxRetries) {
      console.warn(`Max retries (${maxRetries}) exceeded for error boundary`);
      this.showErrorNotification(
        new Error('Maximum retry attempts exceeded'),
        this.props.context
      );
      return;
    }

    // Call custom reset handler
    if (this.props.onReset) {
      this.props.onReset();
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: prevState.retryCount + 1
    }));

    // Debounce rapid retries
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }

    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
    }, 1000);
  };

  private shouldShowError(): boolean {
    if (this.props.showError === false) {
      return false;
    }
    return this.state.hasError;
  }

  private renderFallback(): ReactNode {
    const { fallback: fallbackProp } = this.props;
    const { error, errorInfo, errorId, retryCount } = this.state;

    if (React.isValidElement(fallbackProp)) {
      return fallbackProp;
    }

    if (typeof fallbackProp === 'function') {
      const FallbackComponent = fallbackProp as ComponentType<FallbackProps>;
      return (
        <FallbackComponent
          error={error}
          errorInfo={errorInfo}
          resetError={this.resetError}
          errorId={errorId}
          retryCount={retryCount}
        />
      );
    }

    return (
      <DefaultFallback
        error={error}
        errorInfo={errorInfo}
        resetError={this.resetError}
        errorId={errorId}
        retryCount={retryCount}
      />
    );
  }

  render(): ReactNode {
    if (this.shouldShowError()) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

// HOC for wrapping components with ErrorBoundary
export function withErrorBoundary<P extends object>(
  Component: ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): ComponentType<P> {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}